import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Grid, InputBase, Theme, CircularProgress, IconButton } from '@mui/material'
import { makeStyles } from '@mui/styles'

import ProfileImage from '../ProfileImage'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { IPostElementProps } from '../../../../../types/types'
import { useDispatch, useSelector } from 'react-redux'
import { AnswerToFacebookPostCommentRequest, WriteFacebookPostCommentRequest } from '@3m5/sm_publish-frontend-api'
import {
  answerToFacebookPostCommentInitiated,
  writeFacebookPostCommentInitiated,
} from '../../../../../../store/facebook/facebook.actions'
import { WriteInstagramPostCommentRequest } from '@3m5/sm_publish-frontend-api'
import { writeIgPostCommentInitiated } from '../../../../../../store/instagram/instagram.actions'
import { NetworkType } from '@3m5/sm_publish-frontend-api'
import { namedRequestData } from '../../../../../../store/common/common.selectors'
import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import { PostingRootState } from '../../../../../../store/postingRoot.reducer'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
  },
  inputFieldWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    backgroundColor: '#ebebeb',
  },
  inputField: {
    display: 'flex',
    height: 'auto',
    width: '100%',
    padding: '5px 5px 5px 0',
    justifyContent: 'flex-end',
    alignItems: 'center',
    borderRadius: '18px',
  },
  inputCaret: {
    caretColor: 'transparent',
  },
  iconsList: {
    display: 'flex',
    height: '36px',
    marginLeft: '-8px',
    marginRight: '4px',
    alignSelf: 'flex-end',
    alignItems: 'center',

    listStyleType: 'none',
    margin: 0,
    padding: 0,
  },
  icons: {
    display: 'inline-block',
    alignSelf: 'center',
    height: '16px',
    width: '16px',
    backgroundImage: 'url(/images/preset.png)',
    backgroundSize: 'auto',
    backgroundRepeat: 'no-repeat',
    marginLeft: '6px',
    marginRight: '6px',
  },
  icon1: { backgroundPosition: '0 -455px' },
  icon2: { backgroundPosition: '0 -472px' },
  icon3: { backgroundPosition: '0 -523px' },
  icon4: { backgroundImage: 'url(/images/preset2.png)', backgroundPosition: '0 -50px' },
}))

const NewComment: React.FC<IPostElementProps> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [comment, setComment] = useState('')

  const dispatchWriteIgPostComment = useCallback((params: WriteInstagramPostCommentRequest) => dispatch(writeIgPostCommentInitiated(params)), [dispatch])
  const dispatchWritePostComment = useCallback((params: WriteFacebookPostCommentRequest) => dispatch(writeFacebookPostCommentInitiated(params)), [dispatch])
  const dispatchAnswerToPostComment = useCallback((params: AnswerToFacebookPostCommentRequest) => dispatch(answerToFacebookPostCommentInitiated(params)), [dispatch])

  const actionOnWriteFComment: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, writeFacebookPostCommentInitiated.type))
  const actionOnWriteIComment: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, writeIgPostCommentInitiated.type))

  useEffect(() => {
    if (actionOnWriteFComment?.status === 'success' || actionOnWriteIComment?.status === 'success') {
      comment && props.onChange && props.onChange({ createdAt: new Date(), comment, postId: props.post.networkPostId })
      setComment('')
    }
  }, [actionOnWriteFComment, actionOnWriteIComment])

  const setNewComment = (message: string) => {
    // Instagram Comments

    if (props.networkType === NetworkType.Instagram && props.networkPageId) {
      const params: WriteInstagramPostCommentRequest = {
        networkPostId: props.post.networkPostId,
        networkPageId: props.networkPageId,
        instagramMessageRequest: {
          message,
        },
      }
      dispatchWriteIgPostComment(params)
    }

    // Facebook Comments
    if (props.networkType === NetworkType.Facebook && props.networkPageId) {
      if (props.post.networkCommentId && props.post.networkPostId) {
        const params: AnswerToFacebookPostCommentRequest = {
          networkPostId: props.post.networkPostId,
          networkCommentId: props.post.networkCommentId,
          networkPageId: props.networkPageId,
          answerRequest: {
            message,
          },
        }
        dispatchAnswerToPostComment(params)
      } else {
        const params: WriteFacebookPostCommentRequest = {
          networkPostId: props.post.networkPostId,
          networkPageId: props.networkPageId,
          messageRequest: {
            message,
          },
        }
        dispatchWritePostComment(params)
      }
    }
  }

  const handleSetComment = () => {
    comment && setNewComment(comment)
  }

  return (
    <>
      <Grid
        container
        direction='row'
      >
        <Grid container item xs={12}>
          <Box component='div' className={classes.inputFieldWrapper}>
            <ProfileImage
              style={{ margin: '10px 16px', alignSelf: 'self-start' }}
              data={props.value ? props.value : `https://graph.facebook.com/${props.post && props.post.postedByNetworkId}/picture`}
              view={props.view}
            />
            <div className={classes.inputField}>
              <InputBase
                value={comment}
                placeholder={t('networkContentDashboard:commentPreview')}
                onChange={(e) => setComment(e.target.value)}
                endAdornment={(actionOnWriteFComment?.inProgress || actionOnWriteIComment?.inProgress) && (actionOnWriteFComment?.id === props.post.networkPostId || actionOnWriteIComment?.id === props.post.networkPostId)
                  ? <Box sx={{ p: '5px 8px', display: 'flex', alignItems: 'center' }}><CircularProgress size={24} thickness={3} /></Box>
                  : <IconButton onClick={() => handleSetComment()}><SendRoundedIcon /></IconButton>}
                fullWidth
                multiline
              />
            </div>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}

export default NewComment
